import { useState } from 'react';
import type { Task } from '@/mocks/tasks';

interface TaskItemProps {
  task: Task;
  onToggle: (id: string) => void;
  onEdit?: (id: string) => void;
  onPostpone?: (id: string) => void;
  onDelete?: (id: string) => void;
  compact?: boolean;
}

const priorityConfig: Record<string, { color: string; label: string }> = {
  '高': { color: 'bg-red-100 text-red-700', label: '高' },
  '中': { color: 'bg-amber-100 text-amber-700', label: '中' },
  '低': { color: 'bg-secondary-100 text-secondary-600', label: '低' },
};

export default function TaskItem({ task, onToggle, onEdit, onPostpone, onDelete, compact }: TaskItemProps) {
  const [showActions, setShowActions] = useState(false);

  const isOverdue = !task.completed && new Date(task.dueDate) < new Date(new Date().toDateString());

  return (
    <div
      className={`relative flex items-start gap-3 px-4 py-3 transition-all duration-200 group ${
        task.completed ? 'opacity-50' : ''
      }`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Checkbox */}
      <button
        onClick={() => onToggle(task.id)}
        className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all duration-200 cursor-pointer ${
          task.completed
            ? 'bg-accent-500 border-accent-500 animate-check-pop'
            : 'border-foreground-300 hover:border-primary-400'
        }`}
      >
        {task.completed && (
          <i className="ri-check-line text-white text-xs"></i>
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm ${task.completed ? 'line-through text-foreground-400' : 'text-foreground-800'} font-medium`}>
            {task.title}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${priorityConfig[task.priority].color}`}>
            {priorityConfig[task.priority].label}
          </span>
        </div>
        {!compact && (
          <div className="flex items-center gap-2 mt-1 text-xs text-foreground-400">
            {task.projectName && (
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-folder-3-line text-[10px]"></i>
                </span>
                {task.projectName}
              </span>
            )}
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-time-line text-[10px]"></i>
              </span>
              <span className={isOverdue ? 'text-red-500 font-medium' : ''}>
                {task.dueDate} {task.dueTime && task.dueTime}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* More button - always visible on mobile */}
      {!task.completed && (onEdit || onPostpone || onDelete) && !compact && (
        <button
          onClick={() => setShowActions(!showActions)}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0 mt-0.5 md:hidden whitespace-nowrap"
        >
          <i className="ri-more-2-fill text-sm text-foreground-400"></i>
        </button>
      )}

      {/* Actions menu - hover on desktop */}
      {showActions && !task.completed && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 bg-background-50 rounded-lg px-1 py-1 shadow-sm border border-background-200/70 animate-fade-in z-10">
          {onEdit && (
            <button
              onClick={() => onEdit(task.id)}
              className="w-7 h-7 flex items-center justify-center rounded text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line text-sm"></i>
            </button>
          )}
          {onPostpone && (
            <button
              onClick={() => onPostpone(task.id)}
              className="w-7 h-7 flex items-center justify-center rounded text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-go-forward-line text-sm"></i>
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(task.id)}
              className="w-7 h-7 flex items-center justify-center rounded text-foreground-500 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
          )}
        </div>
      )}
    </div>
  );
}